import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import adminApi from '../utils/adminApi';

const DemandList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ item: '', qty: '', note: '' });
  const [showClear, setShowClear] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const r = await adminApi.get('/demand-list');
      setItems(r.data?.items || []);
    } catch (err) {
      toast.error('Could not load demand list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    inputRef.current?.focus();
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.item.trim()) {
      toast.error('Please write an item name');
      return;
    }
    setSaving(true);
    try {
      await adminApi.post('/demand-list', {
        item: form.item.trim(),
        qty: form.qty.trim() || undefined,
        note: form.note.trim() || undefined,
      });
      setForm({ item: '', qty: '', note: '' });
      inputRef.current?.focus();
      await load();
      toast.success('Added to demand list');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add');
    } finally {
      setSaving(false);
    }
  };

  const toggleBought = async (it) => {
    try {
      await adminApi.patch(`/demand-list/${it.id}`, { bought: !it.bought });
      await load();
    } catch {
      toast.error('Could not update');
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.item}"?`)) return;
    try {
      await adminApi.delete(`/demand-list/${it.id}`);
      await load();
    } catch {
      toast.error('Could not delete');
    }
  };

  const clearBought = async () => {
    try {
      const r = await adminApi.delete('/demand-list?only_bought=true');
      toast.success(`Cleared ${r.data?.deleted || 0} bought items`);
      setShowClear(false);
      await load();
    } catch {
      toast.error('Could not clear');
    }
  };

  const pending = items.filter((i) => !i.bought);
  const bought = items.filter((i) => i.bought);

  const doPrint = () => {
    // Use the browser print — the @media print CSS below shows only the printable area.
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="demand-list-page">
      {/* SCREEN-ONLY: Top bar */}
      <div className="no-print bg-slate-900/80 backdrop-blur border-b border-slate-800 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/staff')}
              className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition"
              data-testid="demand-back-btn"
              title="Back to dashboard"
            >
              <i className="fas fa-arrow-left"></i>
            </button>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-amber-400">Admin</p>
              <h1 className="text-2xl font-black tracking-tight">Demand List</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={doPrint}
              disabled={pending.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 font-bold rounded-lg transition"
              data-testid="demand-print-btn"
            >
              <i className="fas fa-print"></i>
              <span>Print shopping list</span>
            </button>
            {bought.length > 0 && (
              <button
                onClick={() => setShowClear(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold rounded-lg transition"
                data-testid="demand-clear-bought-btn"
              >
                <i className="fas fa-broom"></i>
                <span>Clear bought</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="no-print max-w-5xl mx-auto px-6 py-8">
        <p className="text-slate-400 max-w-2xl mb-6">
          Jot down things customers keep asking for. Print the list, take it shopping, tick them off when they arrive.
        </p>

        {/* Add form */}
        <form onSubmit={submit} className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5 mb-8 grid grid-cols-1 md:grid-cols-[1.5fr_0.6fr_1.2fr_auto] gap-3 items-end" data-testid="demand-add-form">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1 block">Item *</label>
            <input
              ref={inputRef}
              type="text"
              value={form.item}
              onChange={(e) => setForm((f) => ({ ...f, item: e.target.value }))}
              placeholder="e.g. iPhone 15 case, USB-C charger"
              className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm focus:border-amber-500 focus:outline-none"
              maxLength={200}
              data-testid="demand-item-input"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1 block">Qty <span className="text-slate-600 normal-case">(opt.)</span></label>
            <input
              type="text"
              value={form.qty}
              onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))}
              placeholder="3 pcs"
              className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm focus:border-amber-500 focus:outline-none"
              maxLength={60}
              data-testid="demand-qty-input"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1 block">Note <span className="text-slate-600 normal-case">(opt.)</span></label>
            <input
              type="text"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="Sarah asked yesterday"
              className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm focus:border-amber-500 focus:outline-none"
              maxLength={400}
              data-testid="demand-note-input"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 font-black rounded-lg disabled:opacity-60 transition h-[42px]"
            data-testid="demand-add-btn"
          >
            {saving ? <i className="fas fa-spinner fa-spin"></i> : <><i className="fas fa-plus"></i><span>Add</span></>}
          </button>
        </form>

        {loading ? (
          <div className="text-center py-16 text-slate-500"><i className="fas fa-spinner fa-spin mr-2"></i>Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-slate-900/40 border border-dashed border-slate-800 rounded-2xl" data-testid="demand-empty">
            <i className="fas fa-clipboard-list text-4xl text-slate-700 mb-3"></i>
            <p className="text-slate-400">No items yet. Add the first thing a customer asked for.</p>
          </div>
        ) : (
          <>
            {/* Pending */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <h2 className="text-lg font-bold">To buy</h2>
                <span className="px-2.5 py-0.5 bg-amber-500/20 text-amber-300 text-xs font-bold rounded-full" data-testid="demand-pending-count">{pending.length}</span>
              </div>
              {pending.length === 0 ? (
                <p className="text-slate-500 text-sm italic">Nothing pending — nice.</p>
              ) : (
                <ul className="divide-y divide-slate-800 bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden" data-testid="demand-pending-list">
                  {pending.map((it) => (
                    <li key={it.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-900/80 transition" data-testid={`demand-item-${it.id}`}>
                      <button
                        onClick={() => toggleBought(it)}
                        className="w-6 h-6 rounded border-2 border-slate-600 hover:border-amber-400 hover:bg-amber-400/10 flex items-center justify-center transition shrink-0"
                        title="Mark as bought"
                        data-testid={`demand-toggle-${it.id}`}
                      ></button>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">
                          {it.item}
                          {it.qty && <span className="ml-2 text-xs text-amber-400 font-bold">×{it.qty}</span>}
                        </p>
                        {it.note && <p className="text-xs text-slate-500 truncate">{it.note}</p>}
                      </div>
                      <button
                        onClick={() => remove(it)}
                        className="text-slate-500 hover:text-red-400 transition p-1"
                        title="Delete"
                        data-testid={`demand-delete-${it.id}`}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Bought */}
            {bought.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="text-lg font-bold text-slate-400">Bought</h2>
                  <span className="px-2.5 py-0.5 bg-slate-800 text-slate-400 text-xs font-bold rounded-full">{bought.length}</span>
                </div>
                <ul className="divide-y divide-slate-800/60 bg-slate-900/30 border border-slate-800/60 rounded-xl overflow-hidden">
                  {bought.map((it) => (
                    <li key={it.id} className="flex items-center gap-3 px-4 py-3 opacity-60 hover:opacity-100 transition">
                      <button
                        onClick={() => toggleBought(it)}
                        className="w-6 h-6 rounded border-2 border-emerald-500 bg-emerald-500 flex items-center justify-center shrink-0"
                        title="Mark as still needed"
                      >
                        <i className="fas fa-check text-slate-900 text-xs"></i>
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold line-through truncate">
                          {it.item}
                          {it.qty && <span className="ml-2 text-xs text-slate-500 font-bold">×{it.qty}</span>}
                        </p>
                        {it.note && <p className="text-xs text-slate-600 truncate">{it.note}</p>}
                      </div>
                      <button onClick={() => remove(it)} className="text-slate-600 hover:text-red-400 transition p-1" title="Delete">
                        <i className="fas fa-times"></i>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      {/* PRINT-ONLY area */}
      <div className="print-only">
        <div className="print-header">
          <h1>Ashop — Shopping List</h1>
          <p>{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        {pending.length === 0 ? (
          <p style={{ marginTop: 24 }}>No items to buy.</p>
        ) : (
          <table className="print-table">
            <thead>
              <tr>
                <th style={{ width: '36px' }}></th>
                <th>Item</th>
                <th style={{ width: '80px' }}>Qty</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((it, idx) => (
                <tr key={it.id}>
                  <td style={{ textAlign: 'center' }}>☐</td>
                  <td><strong>{it.item}</strong></td>
                  <td>{it.qty || '—'}</td>
                  <td>{it.note || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="print-footer">Ashop Rotterdam · Schiedamseweg 159A · Total items: {pending.length}</p>
      </div>

      {/* Clear-bought confirm */}
      {showClear && (
        <div className="no-print fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowClear(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-2">Clear bought items?</h3>
            <p className="text-sm text-slate-400 mb-5">This will permanently remove {bought.length} bought item{bought.length === 1 ? '' : 's'} from the list.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowClear(false)} className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg font-semibold">Cancel</button>
              <button onClick={clearBought} className="flex-1 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-lg font-semibold" data-testid="demand-clear-confirm-btn">Clear</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .print-only { display: none; }
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; padding: 24px 32px; color: #000; background: #fff; }
          .print-only h1 { font-size: 22px; margin: 0; font-weight: 800; }
          .print-header { border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 16px; }
          .print-header p { font-size: 12px; color: #444; margin-top: 4px; }
          .print-table { width: 100%; border-collapse: collapse; font-size: 13px; }
          .print-table th, .print-table td { border: 1px solid #999; padding: 8px 10px; text-align: left; vertical-align: top; }
          .print-table th { background: #f2f2f2; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; }
          .print-footer { margin-top: 24px; font-size: 11px; color: #555; text-align: center; border-top: 1px solid #ccc; padding-top: 10px; }
          body { background: #fff !important; }
        }
      `}</style>
    </div>
  );
};

export default DemandList;
